import { MonitoringContext, MonitoringMessageOptions, MonitoringModule } from './monitoring.interface';

const toConsoleMethod: Record<MonitoringMessageOptions['level'], 'error' | 'warn' | 'info' | 'log' | 'debug'> = {
  fatal: 'error',
  error: 'error',
  warning: 'warn',
  log: 'log',
  info: 'info',
  debug: 'debug',
};

/**
 * Appended as a trailing argument to error-level console output so RUM (which auto-collects
 * `console.error`) can recognise and drop these — they are duplicates of the error the Datadog
 * backend already reports via `addError`. Genuine third-party `console.error` calls lack this marker
 * and are still collected. See the RUM `beforeSend` filter in `datadog-client-monitoring`.
 */
export const MONITORED_CONSOLE_MARKER = '[fcom-monitored]';

/**
 * Builds a console backend that mirrors monitoring signals to the console when `enabled` (typically
 * local/test). One place owns console output, keeping it out of the Sentry/Datadog backends.
 */
export const createConsoleMonitoring = (enabled: boolean): MonitoringModule => ({
  init: () => undefined,
  captureException: (error: unknown) => {
    if (enabled) {
      console.error(error, MONITORED_CONSOLE_MARKER);
    }
  },
  captureMessage: (message: string, options: MonitoringMessageOptions) => {
    if (enabled) {
      const method = toConsoleMethod[options.level];
      if (method === 'error') {
        console.error(message, options.extra ?? {}, MONITORED_CONSOLE_MARKER);
      } else {
        console[method](message, options.extra ?? {});
      }
    }
  },
  action: (name: string, context?: MonitoringContext) => {
    if (enabled) {
      console.info(`[action] ${name}`, context ?? {});
    }
  },
});
